// src/components/TrendingPosts.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import { db } from "../lib/firebase";
import "./TrendingPosts.css";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";

export default function TrendingPosts({
  posts = [],
  title,
  description,
  seeMoreLink, // 🔹 parent can pass /gallery/design-ideas (category-level)
  fontStyles,
  sectionStyles = {},
}) {
  const isMobile = useIsMobile();
  const isCompact = useIsMobile(640);
  // ---- Read Dynamic Footer Background Color ----
  const [footerColor, setFooterColor] = useState("#111827");

  useEffect(() => {
    const off = onValue(ref(db, "footerconfig/backgroundcolor"), (snap) => {
      if (snap.exists()) setFooterColor(snap.val());
    });
    return () => off();
  }, []);

  // ---- Normalize posts ----
  const data =
    Array.isArray(posts) && posts.length
      ? posts.map((p, i) => ({
          image: p.image || p.img || "/placeholder.jpg",
          title: p.title || p.name || `Post ${i + 1}`,
          link: p.link || p.href || "", // should already be /gallery/... from parent
          date: p.date || "",
        }))
      : [];

  const heading = title || "Stay updated with trending home interior designs!";
  const subheading =
    description ||
    "Find everything from design fixes to expert tips on magazine.";

  const sectionTitleFont = fontStyles?.sectionTitle || fontStyles?.heading || {};
  const sectionDescFont = fontStyles?.sectionDescription || fontStyles?.subHeading || {};

  const sizeOverrides = sectionStyles?.fontSizes || {};

  const headingStyle = {
    fontFamily: sectionTitleFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.heading || sectionTitleFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionTitleFont?.fontWeight || 700,
    lineHeight: sectionTitleFont?.lineHeight || "1.2",
    color: sectionStyles?.headingColor || sectionTitleFont?.color || undefined,
  };

  const subheadingStyle = {
    fontFamily: sectionDescFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.subHeading || sectionDescFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionDescFont?.fontWeight || 400,
    lineHeight: sectionDescFont?.lineHeight || "1.6",
    color: sectionStyles?.subHeadingColor || sectionDescFont?.color || undefined,
  };

  const linkStyle = {
    fontFamily: fontStyles?.button?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.button || fontStyles?.button?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.button?.fontWeight || 600,
    color:
      sectionStyles?.buttonTextColor ||
      sectionStyles?.contentColor ||
      fontStyles?.button?.color ||
      undefined,
  };

  const cardTitleStyle = {
    fontFamily: fontStyles?.subHeading?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.content || fontStyles?.subHeading?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.subHeading?.fontWeight || 600,
    lineHeight: fontStyles?.subHeading?.lineHeight || "1.4",
    color:
      sectionStyles?.contentColor ||
      fontStyles?.subHeading?.color ||
      undefined,
  };

  const metaStyle = {
    fontFamily: fontStyles?.content?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.content || fontStyles?.content?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.content?.fontWeight || 400,
    lineHeight: fontStyles?.content?.lineHeight || "1.4",
    color:
      sectionStyles?.contentColor ||
      fontStyles?.content?.color ||
      undefined,
  };

  // ---- Compute primary "See more" link ----
  // 1) Prefer seeMoreLink from parent (for category-level: /gallery/design-ideas)
  // 2) Else fall back to first valid post.link (for subcategory: /gallery/design-ideas/living-room)
  // 3) If nothing, hide the See more link
  const firstValidLink =
    data.find((p) => p.link && p.link !== "#")?.link || null;

  const primaryLink = seeMoreLink || firstValidLink || null;

  // ---- Show more / less ----
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setShowAll(false);
  }, [isCompact]);

  const initialLimit = isCompact ? 4 : 6;
  const visiblePosts = showAll ? data : data.slice(0, initialLimit);

  return (
    <section className="trend-wrap">
      {/* ===== Header ===== */}
      <div className="trend-head">
        <div>
          <h2 style={headingStyle}>{heading}</h2>
          <p style={subheadingStyle}>{subheading}</p>
        </div>

        {primaryLink && (
          <Link className="trend-more" to={primaryLink} style={linkStyle}>
            See more →
          </Link>
        )}
      </div>

      {/* ===== Grid ===== */}
      <div className="trend-grid">
        {visiblePosts.map((p, i) => (
          <article key={i} className="trend-card">
            <Link className="trend-media" to={p.link || "#"}>
              <img src={p.image} alt={p.title} loading="lazy" />
            </Link>

            <Link className="trend-title" to={p.link || "#"}>
              <span style={cardTitleStyle}>{p.title}</span>
            </Link>

            {p.date && <p className="trend-meta" style={metaStyle}>{p.date}</p>}
          </article>
        ))}
      </div>

      {/* ===== Show more / less button ===== */}
      {data.length > initialLimit && (
        <div className="trend-toggle-wrap">
          <button
            type="button"
            className="trend-toggle"
            onClick={() => setShowAll((v) => !v)}
            style={{ backgroundColor: footerColor, ...linkStyle }}
          >
            {showAll ? "Show less" : "Show more"}
          </button>
        </div>
      )}
    </section>
  );
}
